import moment from 'moment';

/**
 * Clase base para estrategias de exportación de reportes
 * Cada tipo de reporte (Financiamiento, ANR, Info Mora, etc.)
 * extenderá esta clase para definir su configuración específica
 */
export default class BaseExportStrategy {
  constructor(reportType) {
    this.reportType = reportType;
  }

  // ==========================================
  // MÉTODOS A SOBRESCRIBIR POR CADA REPORTE
  // ==========================================

  /**
   * Devuelve el título del reporte (puede ser sobrescrito por cada estrategia)
   * @param {Object} context - Contexto con configuración
   * @returns {String}
   */
  getTitle(context) {
    // Por defecto, usar el nombre del tipo de reporte
    // Las estrategias pueden sobrescribir este método
    return this.reportType ? this.reportType.toUpperCase() : 'INFORME DE SOLICITUDES';
  }

  /**
   * Devuelve los metadatos del reporte (título, subtítulo, fechas)
   * @param {Array} data - Datos del reporte
   * @param {Object} context - Contexto con helpers y configuración
   * @returns {Object} { title, subtitle, operatorias, fechaConsulta, fechaGeneracion }
   */
  getMetadata(data, context) {
    const fideicomisosUnicos = this.extractUniqueFideicomisos(data);
    const operatoriasUnicas = this.extractUniqueOperatorias(data);

    return {
      title: this.getTitle(context),
      subtitle: this.getFideicomisoText(fideicomisosUnicos),
      operatorias: this.getOperatoriasText(operatoriasUnicas),
      fechaConsulta: context.fechaReporte,
      fechaGeneracion: moment().format('DD/MM/YYYY HH:mm')
    };
  }

  /**
   * Define las columnas del reporte con su configuración
   * @param {Object} options - Opciones de configuración
   * @returns {Array} Array de columnas con formato:
   * [{
   *   key: 'legajo',
   *   header: 'Legajo',
   *   pdfWidth: 15,
   *   excelWidth: 12,
   *   align: 'left|right|center',
   *   format: (item, ctx) => valor,
   *   formatSubtotal: (item, ctx) => valor,
   *   formatTotal: (items, ctx) => valor,
   *   calculateTotal: (items, ctx) => valor
   * }]
   */
  getColumns(options = {}) {
    throw new Error('getColumns() debe ser implementado por la clase hija');
  }

  /**
   * Determina si una columna debe incluirse en el reporte
   * @param {String} columnKey - Key de la columna
   * @param {Object} options - Opciones del reporte
   * @returns {Boolean}
   */
  shouldIncludeColumn(columnKey, options) {
    return true;
  }

  /**
   * Devuelve los estilos para filas especiales (subtotales, totales)
   * @returns {Object}
   */
  getRowStyles() {
    return {
      subtotal: {
        pdf: {
          fillColor: [230, 240, 255],  // Azul claro
          fontStyle: 'bold',
          textColor: [0, 70, 150],     // Azul oscuro
          fontSize: 6.5
        },
        excel: {
          bold: true,
          bgColor: 'E6F0FF',
          fontColor: '004696'
        }
      },
      total: {
        pdf: {
          fillColor: [245, 247, 250],  // Gris claro
          fontStyle: 'bold',
          textColor: [0, 0, 0],
          fontSize: 6.5
        },
        excel: {
          bold: true,
          bgColor: 'F5F7FA',
          fontColor: '000000'
        }
      }
    };
  }

  // ==========================================
  // MÉTODOS HELPERS COMUNES
  // ==========================================

  /**
   * Extrae fideicomisos únicos de los datos
   * @param {Array} data
   * @returns {Array}
   */
  extractUniqueFideicomisos(data) {
    return [...new Set(
      data
        .filter(item => !item._esSubtotal && !item._esFideicomisoHeader && item.fideicomiso)
        .map(item => item.fideicomiso)
    )];
  }

  /**
   * Extrae operatorias/líneas únicas de los datos
   * @param {Array} data
   * @returns {Array}
   */
  extractUniqueOperatorias(data) {
    return [...new Set(
      data
        .filter(item => !item._esSubtotal && !item._esFideicomisoHeader && (item.operatoria || item.operatoria_linea))
        .map(item => item.operatoria || item.operatoria_linea)
    )];
  }

  /**
   * Genera el texto de fideicomisos para el subtítulo
   * @param {Array} fideicomisos
   * @returns {String}
   */
  getFideicomisoText(fideicomisos) {
    if (!fideicomisos || fideicomisos.length === 0) {
      return '';
    }

    const plural = fideicomisos.length > 1 ? 's' : '';
    return `Fideicomiso${plural}: ${fideicomisos.join(', ')}`;
  }

  /**
   * Genera el texto de operatorias para mostrar debajo del fideicomiso
   * Agrupa de a 3 operatorias por línea para evitar que se salga de la hoja
   * @param {Array} operatorias
   * @returns {String}
   */
  getOperatoriasText(operatorias) {
    if (!operatorias || operatorias.length === 0) {
      return '';
    }

    const plural = operatorias.length > 1 ? 's' : '';
    const prefijo = `Operatoria${plural} / Línea${plural}: `;

    // Si hay 3 o menos operatorias, mostrar en una sola línea
    if (operatorias.length <= 3) {
      return prefijo + operatorias.join(', ');
    }

    // Si hay más de 3, agrupar de a 3 por línea
    const lineas = [];
    for (let i = 0; i < operatorias.length; i += 3) {
      const grupo = operatorias.slice(i, i + 3);
      if (i === 0) {
        // Primera línea incluye el prefijo
        lineas.push(prefijo + grupo.join(', '));
      } else {
        // Líneas subsiguientes tienen indentación para alinearse con el texto
        const indent = ' '.repeat(prefijo.length);
        lineas.push(indent + grupo.join(', '));
      }
    }

    return lineas.join('\n');
  }

  /**
   * Formatea un número con separadores de miles
   * @param {Number} numero
   * @returns {String}
   */
  formatoNumero(numero) {
    if (numero === null || numero === undefined) return '0,00';
    const num = parseFloat(numero);
    if (isNaN(num)) return '0,00';
    return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  /**
   * Formatea una fecha
   * @param {String} fecha
   * @returns {String}
   */
  formatDates(fecha) {
    if (!fecha) return '-';

    // Si ya es una fecha formateada, devolverla tal como está
    if (typeof fecha === 'string' && fecha.includes('/')) {
      return fecha;
    }

    // Si es una fecha ISO o timestamp, formatearla
    const momentDate = moment(fecha);
    if (momentDate.isValid()) {
      return momentDate.format('DD/MM/YYYY');
    }

    return fecha || '-';
  }

  /**
   * Filtra datos regulares (sin headers de fideicomiso ni subtotales)
   * @param {Array} data
   * @returns {Array}
   */
  getRegularData(data) {
    return data.filter(item => !item._esFideicomisoHeader && !item._esSubtotal);
  }

  /**
   * Filtra datos para mostrar (sin headers de fideicomiso)
   * @param {Array} data
   * @returns {Array}
   */
  getDisplayData(data) {
    return data.filter(item => !item._esFideicomisoHeader);
  }

  /**
   * Obtiene las columnas visibles según las opciones
   * @param {Object} options
   * @returns {Array}
   */
  getVisibleColumns(options = {}) {
    const allColumns = this.getColumns(options);
    return allColumns.filter(col => this.shouldIncludeColumn(col.key, options));
  }

  /**
   * Transforma el resultado de format() para obtener el valor a mostrar
   * @param {*} result - Resultado de format/formatSubtotal/formatTotal
   * @returns {String}
   */
  getDisplayValue(result) {
    return typeof result === 'object' && result.display !== undefined
      ? result.display
      : result;
  }

  /**
   * Transforma el resultado de format() para obtener el valor raw (para Excel)
   * @param {*} result - Resultado de format/formatSubtotal/formatTotal
   * @returns {*}
   */
  getRawValue(result) {
    return typeof result === 'object' && result.raw !== undefined
      ? result.raw
      : result;
  }

  /**
   * Crea un objeto de valor con display y raw
   * @param {String} display - Valor a mostrar (con formato)
   * @param {*} raw - Valor sin formato (para Excel)
   * @returns {Object}
   */
  createValue(display, raw) {
    return { display, raw };
  }

  /**
   * Helper para formatear montos
   * @param {Number} value
   * @returns {Object} { display, raw }
   */
  formatMonto(value) {
    const numValue = parseFloat(value) || 0;
    return this.createValue('$' + this.formatoNumero(numValue), numValue);
  }

  /**
   * Valida si el estado del item permite mostrar el monto otorgado.
   * Solo los estados "Otorgado" y "Cancelado" tienen monto otorgado > 0.
   * @param {Object} item - El item a validar
   * @returns {boolean} - true si el estado es válido para mostrar monto
   */
  esEstadoValidoParaMonto(item) {
    if (!item || !item.estado) return false;
    const estadosValidos = ['Otorgado', 'Cancelado'];
    return estadosValidos.includes(item.estado);
  }

  /**
   * Helper para obtener el tipo de monto de los parametros
   * @param {Object} parametros
   * @param {String} tipoMonto
   * @returns {Number}
   */
  getTipoMonto(parametros, tipoMonto) {
    if (!parametros) return 0;

    const valor = parametros[tipoMonto];
    if (!valor) return 0;

    if (Array.isArray(valor)) {
      return parseFloat(valor[0]) || 0;
    }

    return parseFloat(valor) || 0;
  }

  /**
   * Helper para obtener un desembolso por número
   * @param {Object} item
   * @param {Number} num
   * @returns {Object|null}
   */
  getDesembolso(item, num) {
    if (!item.desembolsos || !Array.isArray(item.desembolsos)) {
      return null;
    }

    return item.desembolsos.find(d => d.numeroDesembolso === num) || null;
  }

  /**
   * Calcula el monto pendiente de desembolso
   * @param {Object} item
   * @returns {Number}
   */
  getPendienteDesembolso(item) {
    const montoAprobado = this.getTipoMonto(item.parametros, 'monto_aprobado') || item.monto_aprobado || 0;

    // Solo calcular monto otorgado si el estado es válido
    let montoOtorgado = 0;
    if (this.esEstadoValidoParaMonto(item)) {
      montoOtorgado = this.getTipoMonto(item.parametros, 'monto') || 0;
    }

    return montoAprobado - montoOtorgado;
  }

  /**
   * Nombre del archivo para exportación
   * @param {String} extension - 'pdf' o 'xlsx'
   * @returns {String}
   */
  getFileName(extension) {
    const reportTypeName = this.reportType || 'informe-solicitudes';
    const sanitizedTypeName = reportTypeName
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');

    return `${sanitizedTypeName}-${moment().format('YYYY-MM-DD')}.${extension}`;
  }
}
